import { Customer } from '../Customer/types';
import { BaseApiRequest, ParsedDate, parseIsoDate } from '@api/Trinium/types';
import { JobCategory } from '../JobCategories/types';
import { ediSlice } from './slice';
import { store } from '@reduxStore';

export interface FetchEdiLoadTenderRequest extends BaseApiRequest {
  status?: keyof typeof EdiStatus;
  modifyDateFrom?: string;
  modifyDateTo?: string;
}

/**
 * Raw Data schema as returned by the API
 */
export interface LoadTenderRaw {
  id: number;
  action: string; // e.g., "A" or "C" or "U"
  status: keyof typeof EdiStatus; // e.g., "Rejected"
  terminalNumber: string;
  ordNo?: string;
  csnId?: string;
  jobCatId: string | null;
  jobCatName: string | null;
  isExport: boolean;
  isLive: boolean;
  isHaz: boolean;
  hazName: string | null;
  isOverweight: boolean;
  custId?: string;
  cust?: Customer;
  custRefNo: string;
  houseBol: string;
  ref: string;
  ediRefNo: string | null;
  respondBy: string;
  availDate: string | null; // ISO 8601
  docDate: string | null; // ISO 8601
  demurrageDate: string | null; // ISO 8601
  cntrId: string | null;
  cntrProviderId: string | null;
  vslId: string | null;
  voyageId: number;
  voyage: string | null;
  voyageEta: string | null;
  cntrSize: number | null;
  cntrType: string | null;
  cntrPcs: number | null;
  cntrWgtLb: number | null;
  cntrContents: string | null;
  cntrSealNo: string | null;
  drvName: string | null;
  rate: number | null;
  fuel: number | null;
  dehireId: string;
  dehire?: EdiLocation | null;
  emptyRelNo: string | null;
  puId: string;
  puRefNo: string | null;
  delvRefNo: string | null;
  availNotes: string | null;
  docNotes: string | null;
  drvNotes: string | null;
  createdDate: string; // ISO 8601
  modUsrCode: string;
  modDate: string; // ISO 8601
  cntrProvider: { name: string | null } | null;
  delvs?: EdiDeliveryLocationRaw[];
  pu?: EdiLocation | null;
  rejectReasons?: { id: string; description: string }[];
  vsl?: { name: string | null } | null;
}

/** API response Properties that will be transformed into strongly Typed Properties */
type OmittedLoadTenderProperties =
  'jobCatId' | 'jobCatName' | 'availDate' | 'docDate' | 'demurrageDate' | 'createdDate' | 'modDate' | 'delvs' | 'rejectReasons' | 'action';

/**
 * EDI Load Tender shape
 */
export type LoadTender = Omit<LoadTenderRaw, OmittedLoadTenderProperties> & {
  jobCategory: JobCategory | null;
  availableDate?: ParsedDate;
  documentDate?: ParsedDate;
  dmrgDate?: ParsedDate;
  dateCreated?: ParsedDate;
  dateModified?: ParsedDate;
  deliveries: EdiDeliveryLocation[];
  ediRejectReasons: EdiRejectionReason[];
  ediAction: keyof typeof EdiAction;
};

/**
 * Mapping function to parse the raw API response for the Load Tender API
 */
export function transformLoadTenderResponse(apiResponse: LoadTenderRaw): LoadTender {
  const {
    jobCatId,
    jobCatName,
    availDate,
    docDate,
    demurrageDate,
    createdDate,
    modDate,
    delvs,
    rejectReasons,
    action,
    ...persistedFields
  } = apiResponse;

  return {
    ...persistedFields,
    jobCategory: jobCatId ? { id: jobCatId, name: jobCatName, transType: apiResponse.isExport ? 'E' : 'I' } : null,
    availableDate: parseIsoDate(availDate),
    documentDate: parseIsoDate(docDate),
    dmrgDate: parseIsoDate(demurrageDate),
    dateCreated: parseIsoDate(createdDate),
    dateModified: parseIsoDate(modDate),
    deliveries: delvs?.map((loc) => transformEdiDeliveryLocation(loc)) || [],
    ediRejectReasons: rejectReasons ? [...rejectReasons] : [],
    ediAction: parseEdiAction(action)
  } as LoadTender;
}

/**
 * Mapping function to parse the raw API response collection for the Load Tender API
 */
export function transformLoadTenderResponses(apiResponse: LoadTenderRaw[]): LoadTender[] {
  return apiResponse.map((item) => transformLoadTenderResponse(item));
}

export enum EdiAction {
  Add,
  Cancel,
  Update
}

function parseEdiAction(action: string): keyof typeof EdiAction {
  switch (action.toLowerCase()) {
    case 'a':
      return 'Add';
    case 'c':
      return 'Cancel';
    case 'u':
      return 'Update';
    default:
      throw new Error(`Invalid EdiAction could not be parsed: ${action}`);
  }
}

export enum EdiStatus {
  All,
  Imported,
  Accepted,
  'Auto-Accepted',
  Rejected,
  Deleted,
  Error
}

export interface EdiLocation {
  name?: string | null;
  addr1?: string;
  cityId?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string;
  cntryId?: string | null;
  cntryName?: string | null;
  phone?: string;
}

interface EdiDeliveryLocationRaw {
  stopNo: number;
  id: string;
  name: string;
  addr1: string;
  cityId?: string | null;
  city: string;
  state: string;
  zip: string;
  cntryId?: string | null;
  cntryName?: string | null;
  phone: string;
  schdFrom: string; // ISO 8601
  schdTo: string; // ISO 8601
}

type EdiDeliveryLocation = Omit<EdiDeliveryLocationRaw, 'schdFrom' | 'schdTo'> & {
  scheduledFromDate?: ParsedDate;
  scheduledToDate?: ParsedDate;
};

interface EdiRejectionReason {
  id?: string;
  description?: string;
}

function transformEdiDeliveryLocation(location?: EdiDeliveryLocationRaw): EdiDeliveryLocation | undefined {
  if (!location) return undefined;

  const { schdFrom, schdTo, ...persistedFields } = location;
  return {
    ...persistedFields,
    scheduledFromDate: parseIsoDate(schdFrom),
    scheduledToDate: parseIsoDate(schdTo)
  };
}

export interface FetchEdiLoadTenderDetailsRequest extends BaseApiRequest {
  id: number;
}

export interface PatchEdiLoadTenderMutation extends BaseApiRequest {
  formValues: Partial<LoadTenderRaw>;
}

export interface AcceptEdiLoadTenderMutation extends BaseApiRequest {
  ids: number[];
}

export interface RejectEdiLoadTenderMutation extends BaseApiRequest {
  id: number;
  rejectionReasons: string[];
}

export interface DeleteEdiLoadTenderMutation extends BaseApiRequest {
  id: number;
}

export function updateEdiLoadTenderCache(loadTenders: Partial<LoadTender>[], options: { replaceDetail?: boolean } = {}) {
  const { replaceDetail = false } = options;
  const ids = loadTenders.map((t) => t.id).filter((id): id is number => id !== undefined);

  const invalidEndpoints = ediSlice.util.selectInvalidatedBy(
    store.getState(),
    ids.map((id) => ({ type: 'edi' as const, id }))
  );

  return invalidEndpoints.map((currentInvalidEndpoint) => {
    if (currentInvalidEndpoint.endpointName === 'fetchLoadTenders') {
      const originalArgs = currentInvalidEndpoint.originalArgs as FetchEdiLoadTenderRequest;
      const filterStatus = originalArgs.status;

      return store.dispatch(
        ediSlice.util.updateQueryData('fetchLoadTenders', originalArgs, (draft) => {
          /** Iterate in reverse so splicing in-place doesn't shift remaining indices */
          for (let i = draft.length - 1; i >= 0; i--) {
            const cacheEntry = draft[i];
            const update = loadTenders.find((x) => x.id === cacheEntry.id);
            if (!update) continue;

            const newStatus = update.status ?? cacheEntry.status;
            const matchesFilter = !filterStatus || filterStatus === newStatus;
            const matchesTerminal = !update.terminalNumber || update.terminalNumber === originalArgs.terminalNumber;

            if (matchesFilter && matchesTerminal) {
              Object.assign(cacheEntry, update);
            } else {
              draft.splice(i, 1);
            }
          }
        })
      );
    }

    if (currentInvalidEndpoint.endpointName === 'fetchLoadTender') {
      const originalArgs = currentInvalidEndpoint.originalArgs as FetchEdiLoadTenderDetailsRequest;
      const update = loadTenders.find((x) => x.id === originalArgs.id);
      if (!update) return;

      return store.dispatch(
        ediSlice.util.updateQueryData('fetchLoadTender', originalArgs, (draft) => {
          /**
           * Full-record callers must replace so fields the API omits (e.g. cleared
           * locations) don't leave stale values from the previous cache entry.
           */
          if (replaceDetail) return update as LoadTender;
          Object.assign(draft, update);
        })
      );
    }
  });
}
